import React, { useState } from 'react';
import { PageLayout } from '@/shared/layout';
import { Button, Filter, Search, KPICard, Card } from '@/components/ui';
import { useZones } from './hooks/useMaintenanceZones';
import { ZonesDesktopTable } from './components/ZonesDesktopTable';
import { ZoneFormModal } from './components/ZoneFormModal';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { useAuth } from '@/core/auth/AuthContext';
import { AuthButton } from '@/core/permissions/AuthButton';
import type { ProjectZone } from './types/zones.types';
import { toast } from 'sonner';

export default function ZonesPage() {
  const { profile } = useAuth();
  const companyId = profile?.company_id || '';
  
  const [showCreate, setShowCreate] = useState(false);
  const [editZone, setEditZone] = useState<ProjectZone | null>(null);
  const [deleteZone, setDeleteZone] = useState<ProjectZone | null>(null);

  const {
    zones,
    total,
    stats,
    isLoading,
    error,
    filters,
    setFilters,
    deleteZone: executeDelete,
    isDeleting
  } = useZones(companyId);

  const handleSearchChange = (value: string) => {
    setFilters({ ...filters, search: value, page: 1 });
  };

  const handleFilterChange = (key: string, value: string) => {
    setFilters({ ...filters, [key]: value, page: 1 });
  };

  const handleDelete = async () => {
    if (!deleteZone) return;
    try {
      await executeDelete(deleteZone.id);
      setDeleteZone(null);
      toast.success('Đã xóa khu vực thành công');
    } catch (error: any) {
      toast.error('Lỗi khi xóa: ' + error.message);
    }
  };

  return (
    <PageLayout
      title="Quản lý Khu vực (Zones)"
      description="Chia nhỏ dự án thành các đơn vị vận hành thực tế ngoài hiện trường."
      breadcrumbs={[{ label: 'Bảo trì & Bảo dưỡng' }, { label: 'Khu vực' }]}
      isLoading={isLoading}
      headerActions={
        <div className="flex gap-2">
          <Button variant="outline" icon="ri-map-pin-2-line">Xem bản đồ</Button>
          <AuthButton 
            module="maintenance" 
            action="create" 
            variant="primary" 
            icon="ri-add-line" 
            onClick={() => setShowCreate(true)}
          >
            Tạo khu vực mới
          </AuthButton>
        </div>
      }
    >
      <div className="space-y-6">
        {/* KPI Board */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <KPICard
            title="Tổng khu vực"
            value={stats.total}
            icon="ri-layout-grid-line"
            variant="info"
          />
          <KPICard
            title="Khu vực hoạt động"
            value={stats.active}
            icon="ri-checkbox-circle-line"
            variant="success"
          />
          <KPICard
            title="Tổng diện tích"
            value={`${stats.totalArea.toLocaleString()} m²`}
            icon="ri-aspect-ratio-line"
            variant="warning"
          />
          <KPICard
            title="Loại hình đa dạng"
            value={Object.keys(stats.byType).length}
            icon="ri-stack-line"
            variant="info"
          />
        </div>

        <PageLayout.Toolbar>
          <div className="flex flex-wrap gap-4 w-full justify-between items-center">
            <div className="flex gap-3">
              <Search
                placeholder="Tìm tên khu vực, mã zone..."
                value={filters.search}
                onChange={handleSearchChange}
                className="w-80"
              />
              <Filter
                options={[
                  {
                    id: 'status', label: 'Trạng thái', type: 'select', choices: [
                      { value: 'all', label: 'Tất cả trạng thái' },
                      { value: 'active', label: 'Hoạt động' },
                      { value: 'maintenance', label: 'Bảo trì' },
                      { value: 'inactive', label: 'Ngưng' }
                    ]
                  },
                  {
                    id: 'type', label: 'Loại khu vực', type: 'select', choices: [
                      { value: 'all', label: 'Tất cả loại' },
                      { value: 'lawn', label: 'Cỏ' },
                      { value: 'tree', label: 'Cây bóng mát' },
                      { value: 'shrub', label: 'Cây bụi' },
                      { value: 'water', label: 'Mặt nước' }
                    ]
                  }
                ]}
                onFilterChange={handleFilterChange}
              />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" icon="ri-download-line">Xuất danh sách</Button>
            </div>
          </div>
        </PageLayout.Toolbar>

        <Card className="overflow-hidden border-none shadow-sm">
          <ZonesDesktopTable 
            zones={zones} 
            onEdit={setEditZone} 
            onDelete={setDeleteZone} 
          />
        </Card>
      </div>

      <ZoneFormModal
        isOpen={showCreate || !!editZone}
        zone={editZone}
        companyId={companyId}
        onClose={() => {
          setShowCreate(false);
          setEditZone(null);
        }}
      />

      <ConfirmDialog
        isOpen={!!deleteZone}
        title="Xác nhận xóa khu vực"
        message={`Bạn có chắc chắn muốn xóa khu vực "${deleteZone?.zone_name}"? Mọi liên kết với tài sản và tổ đội sẽ bị ảnh hưởng.`}
        confirmText="Xác nhận xóa"
        isDestructive={true}
        isLoading={isDeleting}
        onConfirm={handleDelete}
        onClose={() => setDeleteZone(null)}
      />
    </PageLayout>
  );
}
